import React from 'react';
import { Heading, Text, Flex, Button } from '@chatsdet/ui';
import { Copy, Trash } from '@phosphor-icons/react';
import type { WorkflowNode } from '../../../types/workflow';

const NODE_TYPE_LABELS: Record<string, string> = {
  login: 'Acting as',
  navigate: 'Navigate',
  action: 'Action',
  assertion: 'Assertion',
  wait: 'Wait',
  condition: 'Condition',
  api_sequence: 'API Sequence',
};

interface MultiSelectSummaryProps {
  selectedNodeIds: ReadonlySet<string>;
  nodes: WorkflowNode[];
  onDeleteSelected: () => void;
  onCopySelected?: () => void;
}

export const MultiSelectSummary: React.FC<MultiSelectSummaryProps> = ({
  selectedNodeIds,
  nodes,
  onDeleteSelected,
  onCopySelected,
}) => {
  const selectedNodes = React.useMemo(
    () => nodes.filter((n) => selectedNodeIds.has(n.id)),
    [nodes, selectedNodeIds]
  );

  // Group by type and count
  const typeBreakdown = React.useMemo(() => {
    const counts = new Map<string, number>();
    for (const node of selectedNodes) {
      const type = node.type ?? 'unknown';
      counts.set(type, (counts.get(type) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([type, count]) => ({
        type,
        label: NODE_TYPE_LABELS[type] ?? type,
        count,
      }));
  }, [selectedNodes]);

  return (
    <div className="flex flex-col gap-4 py-4 px-4">
      <div className="border-b border-border pb-4">
        <Heading level={5} className="mb-1">
          {selectedNodeIds.size} nodes selected
        </Heading>
        <Text size="sm" className="text-muted-foreground">
          {typeBreakdown.map((t) => `${t.count} ${t.label}`).join(', ')}
        </Text>
      </div>

      <div className="flex flex-col gap-2">
        {typeBreakdown.map(({ type, label, count }) => (
          <Flex key={type} justify="between" align="center" className="px-1">
            <Text size="sm">{label}</Text>
            <Text size="sm" className="text-muted-foreground">
              {count}
            </Text>
          </Flex>
        ))}
      </div>

      <div className="pt-2 flex flex-col gap-2">
        {onCopySelected && (
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onPress={onCopySelected}
          >
            <Copy size={14} weight="bold" />
            Copy selected ({selectedNodeIds.size})
          </Button>
        )}
        <Button
          variant="destructive"
          size="sm"
          className="w-full"
          onPress={onDeleteSelected}
        >
          <Trash size={14} weight="bold" />
          Delete selected ({selectedNodeIds.size})
        </Button>
      </div>
    </div>
  );
};
